// src/pages/administrators/PurchaseRequestsAdmin.tsx
import React, {useState, useEffect, CSSProperties } from 'react';
import {collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import {db } from '../../firebase';

interface PurchaseRequest {
  id: string;
  itemName: string;
  description: string;
  budget: number;
  contactInfo: string;
  status: 'Pending' | 'Acknowledged' | 'Fulfilled';
  submittedAt: Date;
}

const styles: {[key: string]: CSSProperties } = {
  container: {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
  },
  card: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem',
  },
  heading: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
  },
  searchBar: {
    width: '100%',
    padding: '0.5rem',
    marginBottom: '1rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid #ddd',
  },
  td: {
    padding: '0.75rem',
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
  },
  statusPending: {
    color: '#ffc107',
    fontWeight: 'bold',
  },
  statusAcknowledged: {
    color: '#007bff',
    fontWeight: 'bold',
  },
  statusFulfilled: {
    color: '#28a745',
    fontWeight: 'bold',
  },
  actionButton: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    marginRight: '0.5rem',
    marginBottom: '0.2rem',
  },
  acknowledgeButton: {
    backgroundColor: '#007bff',
  },
  fulfilledButton: {
    backgroundColor: '#28a745',
  },
  deleteButton: {
    backgroundColor: '#dc3545',
  },
  viewButton: {
    backgroundColor: '#6c757d',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    border: 'none',
    background: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
  },
  modalDetail: {
    marginBottom: '1rem',
  },
};

const PurchaseRequestsAdmin = () => {
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'purchaseRequests'));
      const requestsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt.toDate(),
      })) as PurchaseRequest[];
      setRequests(requestsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching purchase requests:", error);
      setLoading(false);
    }
  };

  const updateRequestStatus = async (id: string, newStatus: 'Acknowledged' | 'Fulfilled') => {
    try {
      const requestRef = doc(db, 'purchaseRequests', id);
      await updateDoc(requestRef, {status: newStatus });
      await fetchRequests();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const deleteRequest = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this purchase request?")) {
      try {
        await deleteDoc(doc(db, 'purchaseRequests', id));
        await fetchRequests();
      } catch (error) {
        console.error("Error deleting request:", error);
      }
    }
  };

  const filteredRequests = requests.filter(request =>
    request.itemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    request.contactInfo?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.heading}>User Purchase Requests</h2>
        <input
          type="text"
          placeholder="Search by item name or contact"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchBar}
        />
        {loading ? (
          <p>Loading requests...</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Item Requested</th>
                <th style={styles.th}>Budget</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length > 0 ? (
                filteredRequests.map((request) => (
                  <tr key={request.id}>
                    <td style={styles.td}>
                      <strong>{request.itemName}</strong>
                      <br />
                      <small>{request.description}</small>
                    </td>
                    <td style={styles.td}>
                      {request.budget ? `UGX ${request.budget.toLocaleString()}` : 'N/A'}
                    </td>
                    <td style={styles.td}>{request.contactInfo}</td>
                    <td style={styles.td}>
                      <span
                        style={
                          request.status === 'Acknowledged'
                            ? styles.statusAcknowledged
                            : request.status === 'Fulfilled'
                            ? styles.statusFulfilled
                            : styles.statusPending
                        }
                      >
                        {request.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={{...styles.actionButton, ...styles.viewButton }}
                        onClick={() => setSelectedRequest(request)}
                      >
                        View
                      </button>
                      {request.status === 'Pending' && (
                        <button
                          style={{...styles.actionButton, ...styles.acknowledgeButton }}
                          onClick={() => updateRequestStatus(request.id, 'Acknowledged')}
                        >
                          Acknowledge
                        </button>
                      )}
                      {request.status === 'Acknowledged' && (
                        <button
                          style={{...styles.actionButton, ...styles.fulfilledButton }}
                          onClick={() => updateRequestStatus(request.id, 'Fulfilled')}
                        >
                          Mark as Fulfilled
                        </button>
                      )}
                      <button
                        style={{...styles.actionButton, ...styles.deleteButton }}
                        onClick={() => deleteRequest(request.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={styles.td}>No purchase requests found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selectedRequest && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button style={styles.closeButton} onClick={() => setSelectedRequest(null)}>&times;</button>
            <h3 style={{ borderBottom: '2px solid #ccc', paddingBottom: '0.5rem' }}>Request Details</h3>
            <div style={styles.modalDetail}>
              <strong>Item Name:</strong> {selectedRequest.itemName}
            </div>
            <div style={styles.modalDetail}>
              <strong>Description:</strong> {selectedRequest.description}
            </div>
            <div style={styles.modalDetail}>
              <strong>Budget:</strong> {selectedRequest.budget ? `UGX ${selectedRequest.budget.toLocaleString()}` : 'N/A'}
            </div>
            <div style={styles.modalDetail}>
              <strong>Contact Info:</strong> {selectedRequest.contactInfo}
            </div>
            <div style={styles.modalDetail}>
              <strong>Status:</strong> {selectedRequest.status}
            </div>
            <div style={styles.modalDetail}>
              <strong>Submitted At:</strong> {selectedRequest.submittedAt.toLocaleString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseRequestsAdmin;